import { Box, Breadcrumbs, Link, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { InfoTip } from './InfoTip';

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  /** What the page is for, in an info bubble beside the title (provider and admin portals only). */
  info?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: { label: string; to?: string }[];
  /** Set false when the page manages its own document title (e.g. SEO pages). */
  setDocumentTitle?: boolean;
}

export function PageHeader({ title, subtitle, info, actions, breadcrumbs, setDocumentTitle = true }: PageHeaderProps) {
  useDocumentTitle(setDocumentTitle ? title : null);
  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs sx={{ mb: 1 }} aria-label="breadcrumb">
          {breadcrumbs.map((b) =>
            b.to ? (
              <Link key={b.label} component={RouterLink} to={b.to} underline="hover" color="inherit">
                {b.label}
              </Link>
            ) : (
              <Typography key={b.label} color="text.primary">
                {b.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" component="h1" sx={{ fontSize: { xs: '1.375rem', md: '1.75rem' } }}>
            {title}
            {info && <InfoTip title={info} label={`About ${title}`} />}
          </Typography>
          {subtitle && (
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, '& > *': { m: '0 !important' } }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
